import { saveAs } from "file-saver";
import _ from "lodash";
import moment from "moment";

import {
  getDeviceFamilyFromSerial
} from "./deviceMacFormatValidation";

const ExcelJS = require("exceljs");

export interface DeviceTestRecord {
  id?: string;
  device_id: string;
  result: string;
  createdAt: string | Date;
  clientIp?: string;
  attributes?: {
    wifi?: boolean;
    firmware_version?: string;
    model?: string;
  };
}

interface ExportDeviceTestsProductionReportOptions {
  tests: DeviceTestRecord[];
  reportDate?: Date | string;
  fileName?: string;
}

const BREAKDOWN_TEMPLATE_ROWS = 10;
const BREAKDOWN_COLUMN_COUNT = 6;

const getLogLastRow = (dataRowCount: number) =>
  Math.max(500, Math.ceil(Math.max(dataRowCount, 1) / 500) * 500);

const COLORS = {
  navy: "FF1F2A44",
  grayText: "FF666666",
  grayHeader: "FF555555",
  lightGrayFill: "FFF2F2F2",
  green: "FF1E8449",
  red: "FFC0392B",
  blue: "FF0000FF",
  yellowFill: "FFFFF6D9",
  white: "FFFFFFFF",
  passedFill: "FFD9EAD3",
  failedFill: "FFF4CCCC",
  black: "FF000000",
  borderGray: "FFB7B7B7"
};

const solidFill = (argb: string) => ({
  type: "pattern",
  pattern: "solid",
  fgColor: { argb }
});

const DEVICE_LOG_COLUMN_COUNT = 7;
const LATEST_HELPER_COLUMN = "J";
const RESULT_COLUMN = "F";

const thinBorder = {
  top: { style: "thin", color: { argb: COLORS.borderGray } },
  left: { style: "thin", color: { argb: COLORS.borderGray } },
  bottom: { style: "thin", color: { argb: COLORS.borderGray } },
  right: { style: "thin", color: { argb: COLORS.borderGray } }
};

const applyDeviceLogTableChrome = (
  sheet: any,
  lastRow: number,
  lastColumn = DEVICE_LOG_COLUMN_COUNT
) => {
  for (let row = 1; row <= lastRow; row += 1) {
    for (let column = 1; column <= lastColumn; column += 1) {
      const cell = sheet.getCell(row, column);
      cell.border = thinBorder;
      if (row > 1) {
        centerTableCell(cell);
      }
    }
  }

  sheet.autoFilter = {
    from: { row: 1, column: 1 },
    to: { row: lastRow, column: lastColumn }
  };
};

const applyBreakdownTableBorders = (
  sheet: any,
  headerRow: number,
  lastDataRow: number
) => {
  for (let row = headerRow; row <= lastDataRow; row += 1) {
    for (let column = 1; column <= BREAKDOWN_COLUMN_COUNT; column += 1) {
      const cell = sheet.getCell(row, column);
      cell.border = thinBorder;
      centerTableCell(cell, row === headerRow);
    }
  }
};

const getLatestAttempts = (tests: DeviceTestRecord[]) =>
  _.chain(tests)
    .groupBy("device_id")
    .map((attempts) =>
      _.maxBy(attempts, (test) => new Date(test.createdAt).getTime())
    )
    .compact()
    .value();

const buildBreakdownLabels = (
  latestAttempts: DeviceTestRecord[],
  valueKey: (test: DeviceTestRecord) => string | undefined
) =>
  _(latestAttempts)
    .map(valueKey)
    .compact()
    .uniq()
    .sort()
    .value();

const styleSummaryKpiCell = (
  cell: any,
  color: string,
  numFmt?: string
) => {
  cell.font = { bold: true, size: 20, color: { argb: color } };
  cell.fill = solidFill(COLORS.lightGrayFill);
  cell.alignment = { horizontal: "center" };
  if (numFmt) {
    cell.numFmt = numFmt;
  }
};

const styleDeviceLogHeaderCell = (cell: any) => {
  cell.font = { bold: true, size: 10, color: { argb: COLORS.white } };
  cell.fill = solidFill(COLORS.navy);
  cell.alignment = {
    horizontal: "center",
    vertical: "middle",
    wrapText: true
  };
};

const styleBreakdownHeaderCell = (cell: any) => {
  cell.font = { bold: true, size: 10, color: { argb: COLORS.white } };
  cell.fill = solidFill(COLORS.navy);
  cell.alignment = {
    horizontal: "center",
    vertical: "middle",
    wrapText: true
  };
};

const styleBreakdownLabelCell = (cell: any) => {
  cell.font = { size: 10, color: { argb: COLORS.blue } };
  cell.fill = solidFill(COLORS.yellowFill);
  cell.alignment = { horizontal: "center", vertical: "middle" };
};

const centerTableCell = (cell: any, wrapText = false) => {
  cell.alignment = {
    horizontal: "center",
    vertical: "middle",
    ...(wrapText ? { wrapText: true } : {})
  };
};

const styleBreakdownValueCell = (cell: any, numFmt?: string, color?: string) => {
  cell.font = { size: 10, ...(color ? { color: { argb: color } } : {}) };
  cell.alignment = { horizontal: "center", vertical: "middle" };
  if (numFmt) {
    cell.numFmt = numFmt;
  }
};

const PASS_RATE_NUM_FMT = "0%";

const styleBreakdownPassRateCell = (cell: any) => {
  cell.numFmt = PASS_RATE_NUM_FMT;
  cell.font = { size: 10, color: { argb: COLORS.navy } };
  cell.alignment = { horizontal: "center", vertical: "middle" };
};

const passRateFormula = (passedCell: string, failedCell: string) =>
  `IF(${passedCell}=0,0,${passedCell}/(${passedCell}+${failedCell}))`;

const styleResultCell = (cell: any, result: string) => {
  cell.font = { size: 10, color: { argb: COLORS.black } };
  cell.alignment = { horizontal: "center", vertical: "middle" };

  if (result === "PASSED") {
    cell.fill = solidFill(COLORS.passedFill);
  } else if (result === "FAILED") {
    cell.fill = solidFill(COLORS.failedFill);
  }
};

const DATE_TIME_DISPLAY_LENGTH = 16;
const BOOLEAN_DISPLAY_LENGTH = 5;
const PERCENT_DISPLAY_LENGTH = 8;
const FORMULA_NUMERIC_DISPLAY_LENGTH = 10;

const getCellDisplayLength = (cell: any): number => {
  const value = cell.value;
  let baseLength = 0;

  if (value == null || value === "") {
    baseLength = 0;
  } else if (value instanceof Date) {
    baseLength = DATE_TIME_DISPLAY_LENGTH;
  } else {
    switch (typeof value) {
      case "boolean":
        baseLength = BOOLEAN_DISPLAY_LENGTH;
        break;
      case "number":
        baseLength = String(value).length;
        break;
      case "object":
        if ("formula" in value && value.formula) {
          baseLength = cell.numFmt?.includes("%")
            ? PERCENT_DISPLAY_LENGTH
            : FORMULA_NUMERIC_DISPLAY_LENGTH;
        } else if ("richText" in value && Array.isArray(value.richText)) {
          baseLength = value.richText
            .map((part: { text?: string }) => part.text || "")
            .join("").length;
        } else {
          baseLength = String(value).length;
        }
        break;
      default:
        baseLength = String(value).length;
    }
  }

  const fontSize = cell.font?.size || 10;
  const scale = fontSize / 10;
  let scaledLength = baseLength * scale;

  if (cell.font?.bold) {
    scaledLength *= 1.1;
  }

  return Math.ceil(scaledLength);
};

interface AutoFitColumnOptions {
  minWidth?: number;
  maxWidth?: number;
  padding?: number;
  extraWidth?: number;
}

interface AutoFitRowRange {
  startRow: number;
  endRow: number;
}

const autoFitColumns = (
  sheet: any,
  columnIndexes: number[],
  rowRanges: AutoFitRowRange | AutoFitRowRange[],
  { minWidth = 8, maxWidth = 55, padding = 2, extraWidth = 0 }: AutoFitColumnOptions = {}
) => {
  const ranges = Array.isArray(rowRanges) ? rowRanges : [rowRanges];

  columnIndexes.forEach((columnIndex) => {
    let maxLength = 0;

    ranges.forEach(({ startRow, endRow }) => {
      for (let row = startRow; row <= endRow; row += 1) {
        maxLength = Math.max(
          maxLength,
          getCellDisplayLength(sheet.getCell(row, columnIndex))
        );
      }
    });

    sheet.getColumn(columnIndex).width = Math.min(
      maxWidth,
      Math.max(minWidth, maxLength + padding + extraWidth)
    );
  });
};

const writeBreakdownSection = (
  sheet: any,
  startRow: number,
  title: string,
  labelColumn: string,
  labels: string[],
  deviceLogMatchColumn: "D" | "G",
  logLastRow: number,
  padEmptyRows: boolean
) => {
  const titleCell = sheet.getCell(startRow, 1);
  titleCell.value = title;
  titleCell.font = { bold: true, size: 12, color: { argb: COLORS.navy } };

  const headerRow = startRow + 1;
  [labelColumn, "Tested", "Passed", "Failed", "Untested", "Pass Rate"].forEach(
    (header, index) => {
      const cell = sheet.getCell(headerRow, index + 1);
      cell.value = header;
      styleBreakdownHeaderCell(cell);
    }
  );

  const firstDataRow = startRow + 2;
  const rowCount = padEmptyRows
    ? Math.max(BREAKDOWN_TEMPLATE_ROWS, labels.length)
    : labels.length;

  if (rowCount === 0) {
    applyBreakdownTableBorders(sheet, headerRow, headerRow);
    return headerRow;
  }

  const lastDataRow = firstDataRow + rowCount - 1;

  for (let row = firstDataRow; row <= lastDataRow; row += 1) {
    const label = labels[row - firstDataRow] || "";

    const labelCell = sheet.getCell(row, 1);
    labelCell.value = label || null;
    if (label) {
      styleBreakdownLabelCell(labelCell);
    }

    sheet.getCell(row, 2).value = {
      formula: `IF(A${row}="","",SUMPRODUCT(('Device Log'!$${LATEST_HELPER_COLUMN}$2:$${LATEST_HELPER_COLUMN}$${logLastRow}="Yes")*('Device Log'!$${deviceLogMatchColumn}$2:$${deviceLogMatchColumn}$${logLastRow}=A${row})))`
    };
    sheet.getCell(row, 3).value = {
      formula: `IF(A${row}="","",SUMPRODUCT(('Device Log'!$${LATEST_HELPER_COLUMN}$2:$${LATEST_HELPER_COLUMN}$${logLastRow}="Yes")*('Device Log'!$${deviceLogMatchColumn}$2:$${deviceLogMatchColumn}$${logLastRow}=A${row})*('Device Log'!$${RESULT_COLUMN}$2:$${RESULT_COLUMN}$${logLastRow}="PASSED")))`
    };
    sheet.getCell(row, 4).value = {
      formula: `IF(A${row}="","",SUMPRODUCT(('Device Log'!$${LATEST_HELPER_COLUMN}$2:$${LATEST_HELPER_COLUMN}$${logLastRow}="Yes")*('Device Log'!$${deviceLogMatchColumn}$2:$${deviceLogMatchColumn}$${logLastRow}=A${row})*('Device Log'!$${RESULT_COLUMN}$2:$${RESULT_COLUMN}$${logLastRow}="FAILED")))`
    };
    sheet.getCell(row, 5).value = {
      formula: `IF(A${row}="","",B${row}-C${row}-D${row})`
    };
    sheet.getCell(row, 6).value = {
      formula: `IF(A${row}="","",${passRateFormula(`C${row}`, `D${row}`)})`
    };

    styleBreakdownValueCell(sheet.getCell(row, 2));
    styleBreakdownValueCell(sheet.getCell(row, 3), undefined, COLORS.green);
    styleBreakdownValueCell(sheet.getCell(row, 4), undefined, COLORS.red);
    styleBreakdownValueCell(sheet.getCell(row, 5), undefined, COLORS.grayHeader);
  }

  applyBreakdownTableBorders(sheet, headerRow, lastDataRow);

  for (let row = firstDataRow; row <= lastDataRow; row += 1) {
    styleBreakdownPassRateCell(sheet.getCell(row, 6));
  }

  return lastDataRow;
};

const exportDeviceTestsProductionReport = async ({
  tests,
  reportDate = new Date(),
  fileName = "Daily_Production_Report"
}: ExportDeviceTestsProductionReportOptions) => {
  const sortedTests = _.orderBy(
    tests,
    (test) => new Date(test.createdAt).getTime(),
    "desc"
  );

  const latestAttempts = getLatestAttempts(sortedTests);
  const latestKeys = new Set(
    latestAttempts.map(
      (test) => `${test.device_id}|${new Date(test.createdAt).getTime()}`
    )
  );

  const modelLabels = buildBreakdownLabels(
    latestAttempts,
    (test) => getDeviceFamilyFromSerial(test.device_id)
  );
  const ipLabels = buildBreakdownLabels(
    latestAttempts,
    (test) => test.clientIp
  );

  const logLastRow = getLogLastRow(sortedTests.length);

  const workbook = new ExcelJS.Workbook();
  const deviceLogSheet = workbook.addWorksheet("Device Log");
  const summarySheet = workbook.addWorksheet("Summary");

  summarySheet.orderNo = 1;
  deviceLogSheet.orderNo = 2;

  [
    "Device Serial",
    "WiFi",
    "Firmware Version",
    "Model",
    "Time",
    "Result",
    "IP"
  ].forEach((header, index) => {
    const cell = deviceLogSheet.getCell(1, index + 1);
    cell.value = header;
    styleDeviceLogHeaderCell(cell);
  });

  sortedTests.forEach((test, index) => {
    const rowNumber = index + 2;
    const row = deviceLogSheet.getRow(rowNumber);
    const isLatest = latestKeys.has(
      `${test.device_id}|${new Date(test.createdAt).getTime()}`
    );
    const deviceFamily = getDeviceFamilyFromSerial(test.device_id);

    row.getCell(1).value = test.device_id;
    row.getCell(1).font = { size: 10 };

    row.getCell(2).value = !!test.attributes?.wifi;
    row.getCell(2).font = { size: 10 };

    row.getCell(3).value = test.attributes?.firmware_version || "";
    row.getCell(3).font = { size: 10 };

    row.getCell(4).value = deviceFamily;
    row.getCell(4).font = { size: 10 };

    row.getCell(5).value = new Date(test.createdAt);
    row.getCell(5).font = { size: 10 };
    row.getCell(5).numFmt = "dd/mm/yy hh:mm";

    row.getCell(6).value = test.result;
    styleResultCell(row.getCell(6), test.result);

    row.getCell(7).value = test.clientIp || "";
    row.getCell(7).font = { size: 10 };

    deviceLogSheet.getCell(`J${rowNumber}`).value = isLatest ? "Yes" : "No";
  });

  deviceLogSheet.getColumn(10).hidden = true;

  applyDeviceLogTableChrome(deviceLogSheet, logLastRow);

  deviceLogSheet.getCell("K1").value = "Unique devices:";
  deviceLogSheet.getCell("K1").font = { size: 9, color: { argb: COLORS.grayText } };
  deviceLogSheet.getCell("L1").value = {
    formula: `COUNTIF(${LATEST_HELPER_COLUMN}2:${LATEST_HELPER_COLUMN}${logLastRow},"Yes")`
  };
  deviceLogSheet.getCell("L1").font = { bold: true, size: 9 };

  deviceLogSheet.getCell("K2").value = "Passed (final):";
  deviceLogSheet.getCell("K2").font = { size: 9, color: { argb: COLORS.grayText } };
  deviceLogSheet.getCell("L2").value = {
    formula: `SUMPRODUCT((${LATEST_HELPER_COLUMN}2:${LATEST_HELPER_COLUMN}${logLastRow}="Yes")*(${RESULT_COLUMN}2:${RESULT_COLUMN}${logLastRow}="PASSED"))`
  };
  deviceLogSheet.getCell("L2").font = { bold: true, size: 9, color: { argb: COLORS.green } };

  deviceLogSheet.getCell("K3").value = "Failed (final):";
  deviceLogSheet.getCell("K3").font = { size: 9, color: { argb: COLORS.grayText } };
  deviceLogSheet.getCell("L3").value = {
    formula: `SUMPRODUCT((${LATEST_HELPER_COLUMN}2:${LATEST_HELPER_COLUMN}${logLastRow}="Yes")*(${RESULT_COLUMN}2:${RESULT_COLUMN}${logLastRow}="FAILED"))`
  };
  deviceLogSheet.getCell("L3").font = { bold: true, size: 9, color: { argb: COLORS.red } };

  deviceLogSheet.getCell("K4").value = "Untested (final):";
  deviceLogSheet.getCell("K4").font = { size: 9, color: { argb: COLORS.grayText } };
  deviceLogSheet.getCell("L4").value = {
    formula: `SUMPRODUCT((${LATEST_HELPER_COLUMN}2:${LATEST_HELPER_COLUMN}${logLastRow}="Yes")*(${RESULT_COLUMN}2:${RESULT_COLUMN}${logLastRow}<>"PASSED")*(${RESULT_COLUMN}2:${RESULT_COLUMN}${logLastRow}<>"FAILED"))`
  };
  deviceLogSheet.getCell("L4").font = {
    bold: true,
    size: 9,
    color: { argb: COLORS.grayHeader }
  };

  const deviceLogDataLastRow = sortedTests.length + 1;
  autoFitColumns(
    deviceLogSheet,
    [1, 2, 3, 4, 5, 6, 7],
    { startRow: 1, endRow: deviceLogDataLastRow },
    { extraWidth: 2 }
  );
  deviceLogSheet.getColumn(10).width = 2;
  autoFitColumns(
    deviceLogSheet,
    [11, 12],
    { startRow: 1, endRow: 4 },
    { minWidth: 14, maxWidth: 24 }
  );

  summarySheet.mergeCells("A1:E1");
  summarySheet.mergeCells("A2:E2");

  summarySheet.getCell("A1").value =
    "CoolAutomation — Daily Manufacturing Production Report";
  summarySheet.getCell("A1").font = {
    bold: true,
    size: 16,
    color: { argb: COLORS.navy }
  };
  summarySheet.getCell("A1").alignment = { horizontal: "left", vertical: "middle" };

  summarySheet.getCell("F1").value = "Report Date";
  summarySheet.getCell("F1").font = { bold: true, size: 10 };

  summarySheet.getCell("G1").value = moment(reportDate).toDate();
  summarySheet.getCell("G1").font = {
    bold: true,
    size: 11,
    color: { argb: COLORS.blue }
  };
  summarySheet.getCell("G1").fill = solidFill(COLORS.yellowFill);
  summarySheet.getCell("G1").numFmt = "mm-dd-yy";

  summarySheet.getCell("A2").value =
    "Line: Production QA Test Station   |   Auto-generated from daily test export";
  summarySheet.getCell("A2").font = { size: 10, color: { argb: COLORS.grayText } };
  summarySheet.getCell("A2").alignment = { horizontal: "left", vertical: "middle" };

  summarySheet.getCell("A4").value = "Today's Status at a Glance";
  summarySheet.getCell("A4").font = {
    bold: true,
    size: 12,
    color: { argb: COLORS.navy }
  };

  ["Total Devices Tested", "Passed", "Failed", "Untested", "Pass Rate"].forEach(
    (header, index) => {
      const cell = summarySheet.getCell(5, index + 1);
      cell.value = header;
      cell.font = { bold: true, size: 9, color: { argb: COLORS.grayHeader } };
      cell.fill = solidFill(COLORS.lightGrayFill);
      cell.alignment = { horizontal: "center", vertical: "middle", wrapText: true };
    }
  );

  summarySheet.getCell("A6").value = { formula: "'Device Log'!$L$1" };
  styleSummaryKpiCell(summarySheet.getCell("A6"), COLORS.navy);

  summarySheet.getCell("B6").value = { formula: "'Device Log'!$L$2" };
  styleSummaryKpiCell(summarySheet.getCell("B6"), COLORS.green);

  summarySheet.getCell("C6").value = { formula: "'Device Log'!$L$3" };
  styleSummaryKpiCell(summarySheet.getCell("C6"), COLORS.red);

  summarySheet.getCell("D6").value = { formula: "'Device Log'!$L$4" };
  styleSummaryKpiCell(summarySheet.getCell("D6"), COLORS.grayHeader);

  summarySheet.getCell("E6").value = {
    formula: passRateFormula("B6", "C6")
  };
  const passRateCell = summarySheet.getCell("E6");
  styleSummaryKpiCell(passRateCell, COLORS.navy, PASS_RATE_NUM_FMT);
  passRateCell.numFmt = PASS_RATE_NUM_FMT;

  const afterModelSection = writeBreakdownSection(
    summarySheet,
    8,
    "Breakdown by Model",
    "Model",
    modelLabels,
    "D",
    logLastRow,
    true
  );

  const afterIpSection = writeBreakdownSection(
    summarySheet,
    afterModelSection + 2,
    "Breakdown by IP",
    "IP",
    ipLabels,
    "G",
    logLastRow,
    false
  );

  const modelHeaderRow = 9;
  const ipHeaderRow = afterModelSection + 3;

  autoFitColumns(
    summarySheet,
    [1],
    [
      { startRow: modelHeaderRow, endRow: afterModelSection },
      { startRow: ipHeaderRow, endRow: afterIpSection }
    ],
    { minWidth: 10 }
  );
  autoFitColumns(
    summarySheet,
    [2, 3, 4, 5, 6],
    [
      { startRow: 5, endRow: 6 },
      { startRow: 8, endRow: afterIpSection }
    ],
    { minWidth: 10 }
  );
  autoFitColumns(
    summarySheet,
    [6, 7],
    { startRow: 1, endRow: 1 },
    { minWidth: 12, maxWidth: 18 }
  );

  const buffer = await workbook.xlsx.writeBuffer();
  saveAs(
    new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
    }),
    `${fileName}.xlsx`
  );
};

export default exportDeviceTestsProductionReport;
